import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Field, Input, Textarea, Card, EmptyState } from '../components/ui';
import { Can } from '../components/Can';

type R = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);

/** Parse a bank CSV. Column order is the user's to describe — banks agree on nothing. */
function parseCsv(text: string, map: { date: number; description: number; amount: number; reference: number; balance: number; dateFormat: string }) {
  const rows: Array<{ statementDate: string; description: string; reference: string | null; amountMinor: number; balanceMinor: number | null }> = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  for (const [i, line] of lines.entries()) {
    // Split on commas outside quotes.
    const cells = (line.match(/("([^"]|"")*"|[^,]*)(,|$)/g) ?? []).map((c) => c.replace(/,$/, '').replace(/^"|"$/g, '').replace(/""/g, '"').trim());
    const raw = cells[map.date] ?? '';
    if (!raw || (i === 0 && Number.isNaN(Date.parse(normaliseDate(raw, map.dateFormat))))) continue; // header row
    const date = normaliseDate(raw, map.dateFormat);
    if (Number.isNaN(Date.parse(date))) continue;
    const amount = Number(String(cells[map.amount] ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(amount) || amount === 0) continue;
    const bal = Number(String(cells[map.balance] ?? '').replace(/[^0-9.-]/g, ''));
    rows.push({
      statementDate: date,
      description: cells[map.description] ?? '',
      reference: cells[map.reference] || null,
      amountMinor: Math.round(amount * 100),
      balanceMinor: Number.isFinite(bal) && String(cells[map.balance] ?? '').trim() ? Math.round(bal * 100) : null,
    });
  }
  return rows;
}
function normaliseDate(v: string, format: string): string {
  const parts = v.split(/[/\-.]/).map((p) => p.trim());
  if (parts.length !== 3) return v;
  const [a, b, c] = parts as [string, string, string];
  if (format === 'YMD') return `${a.padStart(4, '20')}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;
  const year = c.length === 2 ? `20${c}` : c;
  return format === 'DMY' ? `${year}-${b.padStart(2, '0')}-${a.padStart(2, '0')}` : `${year}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`;
}

/**
 * Bank reconciliation.
 *
 * Import the statement, match each line to the payment the platform already knows
 * about, or post the ones it does not — bank charges, interest, an unrecorded direct
 * debit — straight to an account. What is left over is the reconciling difference,
 * and the page says so rather than hiding it.
 */
export default function BankingPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [status, setStatus] = useState('UNMATCHED');
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState<R | null>(null);
  const [excluding, setExcluding] = useState<R | null>(null);
  const [note, setNote] = useState('');
  const [accountId, setAccountId] = useState('');
  const [csv, setCsv] = useState('');
  const [colMap, setColMap] = useState({ date: 0, description: 1, amount: 2, reference: 3, balance: 4, dateFormat: 'DMY' });

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<R[]>('/entities')).data });
  const banks = useQuery({ queryKey: ['bank-accounts', entityId], queryFn: async () => (await api.get<R[]>('/bank-accounts', { entityId: entityId || undefined })).data });
  const lines = useQuery({
    queryKey: ['statement-lines', bankAccountId, entityId, status],
    queryFn: async () => (await api.get<R[]>('/banking/statement-lines', { bankAccountId: bankAccountId || undefined, entityId: entityId || undefined, status: status || undefined })).data,
  });
  const recon = useQuery({
    queryKey: ['reconciliation', bankAccountId],
    queryFn: async () => (await api.get<R>('/banking/reconciliation', { bankAccountId, asOf: today() })).data,
    enabled: !!bankAccountId,
  });
  const accounts = useQuery({ queryKey: ['coa-all', entityId], queryFn: async () => (await api.get<R[]>('/accounts', { entityId: entityId || undefined, isPostable: true })).data });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['statement-lines'] }); void qc.invalidateQueries({ queryKey: ['reconciliation'] }); };

  const parsed = useMemo(() => (csv ? parseCsv(csv, colMap) : []), [csv, colMap]);

  const doImport = useMutation({
    mutationFn: async () => (await api.post<R>('/banking/statements/import', { bankAccountId, format: 'CSV', lines: parsed })).data,
    onSuccess: () => { setImporting(false); setCsv(''); refresh(); },
  });
  const match = useMutation({
    mutationFn: async (body: R) => api.post(`/banking/statement-lines/${matching!.id}/match`, body),
    onSuccess: () => { setMatching(null); setAccountId(''); refresh(); },
  });
  const unmatch = useMutation({ mutationFn: async (id: string) => api.post(`/banking/statement-lines/${id}/unmatch`, {}), onSuccess: refresh });
  const exclude = useMutation({ mutationFn: async () => api.post(`/banking/statement-lines/${excluding!.id}/exclude`, { note }), onSuccess: () => { setExcluding(null); setNote(''); refresh(); } });

  const r = recon.data;

  return (
    <div>
      <PageHeader
        title="Banking & reconciliation"
        subtitle="Import the statement, match what the ledger already knows and post what it does not. A re-import of an overlapping statement is deduplicated rather than double counted."
        actions={<Can do="banking.import"><Button icon="Upload" disabled={!bankAccountId} onClick={() => setImporting(true)}>Import statement</Button></Can>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={entityId} onChange={(e) => { setEntityId(e.target.value); setBankAccountId(''); }}>
          <option value="">All companies</option>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
        <Select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
          <option value="">All bank accounts</option>{banks.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Every line</option><option value="UNMATCHED">Unmatched</option><option value="MATCHED">Matched</option><option value="EXCLUDED">Excluded</option>
        </Select>
      </div>

      {r && (
        <Card className="mb-5" title={`Reconciliation — ${r.bankAccountName}`} subtitle={`As at ${r.asOf}`}>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[13px] lg:grid-cols-3">
            {[
              ['Ledger balance', r.glBalanceMinor],
              ['Plus unpresented payments', r.unpresentedPaymentsMinor],
              ['Less deposits in transit', -r.depositsInTransitMinor],
              ['Expected bank balance', r.expectedBankBalanceMinor],
              ['Statement balance', r.statementBalanceMinor],
              ['Unexplained difference', r.unexplainedMinor],
            ].map(([label, value], i) => (
              <div key={String(label)} className={`flex items-center justify-between ${i === 3 || i === 5 ? 'border-t border-ink-100 pt-2 font-medium' : ''}`}>
                <span className="text-ink-500">{label}</span>
                <span className={i === 5 && value ? 'text-red-600' : ''}><Money minor={value as number} currency={r.currency} /></span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-ink-500">
            {r.reconciled
              ? 'Reconciled — every statement line is accounted for and the balances agree.'
              : `${r.unmatchedLineCount} statement line${r.unmatchedLineCount === 1 ? '' : 's'} still to match. A difference with nothing unmatched means something was posted straight to the bank account without a payment behind it.`}
          </p>
        </Card>
      )}

      {!lines.data?.length ? (
        <EmptyState icon="Landmark" title="No statement lines" body="Pick a bank account and import a CSV export from the bank." />
      ) : (
        <div className="surface overflow-hidden">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/70 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-400">
              <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Suggested match</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {lines.data.map((l) => (
                <tr key={l.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap"><DateText value={l.statementDate} /></td>
                  <td className="px-4 py-2.5"><div className="text-ink-800">{l.description}</div>{l.reference && <div className="text-[11px] text-ink-400">{l.reference}</div>}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${l.amountMinor < 0 ? 'text-ink-700' : 'text-emerald-700'}`}><Money minor={l.amountMinor} currency={l.currency} /></td>
                  <td className="px-4 py-2.5"><Badge value={l.status} tone={l.status === 'MATCHED' ? 'good' : l.status === 'EXCLUDED' ? 'neutral' : 'warning'} /></td>
                  <td className="px-4 py-2.5 text-[12px] text-ink-500">
                    {l.matchedPaymentReference ? <span className="font-mono">{l.matchedPaymentReference}</span>
                      : l.suggestions?.length ? <span>{l.suggestions[0].reference} · {l.suggestions[0].counterparty ?? '—'} <span className="text-ink-400">({l.suggestions[0].daysApart}d apart)</span></span>
                        : l.status === 'UNMATCHED' ? <span className="text-ink-300">nothing obvious</span> : l.note ?? ''}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      {l.status === 'UNMATCHED' && <Can do="banking.reconcile"><Button size="sm" onClick={() => setMatching(l)}>Match</Button></Can>}
                      {l.status === 'UNMATCHED' && <Can do="banking.reconcile"><Button size="sm" variant="ghost" onClick={() => setExcluding(l)}>Exclude</Button></Can>}
                      {l.status !== 'UNMATCHED' && <Can do="banking.reconcile"><Button size="sm" variant="ghost" onClick={() => unmatch.mutate(l.id)} disabled={unmatch.isPending}>Undo</Button></Can>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import */}
      <Modal open={importing} onClose={() => setImporting(false)} size="lg" title="Import a bank statement" footer={<>
        <Button variant="secondary" onClick={() => setImporting(false)}>Cancel</Button>
        <Button onClick={() => doImport.mutate()} disabled={!parsed.length || doImport.isPending}>{doImport.isPending ? 'Importing…' : `Import ${parsed.length} line${parsed.length === 1 ? '' : 's'}`}</Button>
      </>}>
        <div className="space-y-4">
          <p className="text-[13px] text-ink-600">Paste the CSV the bank exported. Banks agree on nothing, so tell us which column is which — counting from zero.</p>
          <div className="grid grid-cols-3 gap-3">
            {(['date', 'description', 'amount', 'reference', 'balance'] as const).map((k) => (
              <Field key={k} label={`${k[0]!.toUpperCase()}${k.slice(1)} column`}>
                <Input type="number" min="0" value={colMap[k]} onChange={(e) => setColMap({ ...colMap, [k]: Number(e.target.value) })} className="w-full" />
              </Field>
            ))}
            <Field label="Date format">
              <Select value={colMap.dateFormat} onChange={(e) => setColMap({ ...colMap, dateFormat: e.target.value })} className="w-full">
                <option value="DMY">31/12/2026</option><option value="MDY">12/31/2026</option><option value="YMD">2026-12-31</option>
              </Select>
            </Field>
          </div>
          <Field label="CSV"><Textarea rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="31/07/2026,BAC CR RCT-UK-2026-000101,1450.00,BACS 12345,128450.00" className="w-full font-mono text-[12px]" /></Field>
          {!!parsed.length && (
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3 text-[12px]">
              <p className="mb-1.5 font-medium text-ink-700">{parsed.length} line{parsed.length === 1 ? '' : 's'} parsed — first three:</p>
              {parsed.slice(0, 3).map((p, i) => <div key={i} className="text-ink-600">{p.statementDate} · {p.description} · {(p.amountMinor / 100).toFixed(2)}</div>)}
            </div>
          )}
          {doImport.isError && <p className="text-xs text-red-600">{(doImport.error as Error).message}</p>}
        </div>
      </Modal>

      {/* Match */}
      <Modal open={!!matching} onClose={() => { setMatching(null); setAccountId(''); }} size="lg" title="Match statement line" footer={<>
        <Button variant="secondary" onClick={() => { setMatching(null); setAccountId(''); }}>Cancel</Button>
        <Button onClick={() => match.mutate({ accountId })} disabled={!accountId || match.isPending}>Post to account</Button>
      </>}>
        {matching && (
          <div className="space-y-4">
            <div className="rounded-xl bg-ink-50 px-3.5 py-3 text-[13px]">
              <div className="flex items-center justify-between"><span className="text-ink-700">{matching.description}</span><Money minor={matching.amountMinor} currency={matching.currency} className="font-medium" /></div>
              <div className="text-[11.5px] text-ink-400"><DateText value={matching.statementDate} />{matching.reference ? ` · ${matching.reference}` : ''}</div>
            </div>

            {!!matching.suggestions?.length && (
              <div>
                <p className="mb-2 text-[12px] font-medium text-ink-600">Payments this could be</p>
                <div className="space-y-1.5">
                  {matching.suggestions.map((s: R) => (
                    <button key={s.paymentId} onClick={() => match.mutate({ paymentId: s.paymentId })} disabled={match.isPending}
                      className="flex w-full items-center justify-between rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-left text-[13px] transition hover:border-bronze-300 hover:bg-bronze-50/40">
                      <span><span className="font-mono text-[12px] text-ink-600">{s.reference}</span> <span className="text-ink-700">{s.counterparty ?? '—'}</span><span className="ml-2 text-[11px] text-ink-400"><DateText value={s.receivedOn} /> · {s.daysApart} day{s.daysApart === 1 ? '' : 's'} apart</span></span>
                      <Money minor={s.amountMinor} currency={s.currency} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="mb-2 text-[12px] font-medium text-ink-600">Or post it — for what nobody entered</p>
              <p className="mb-2 text-[12px] text-ink-500">Bank charges, interest, an unrecorded direct debit. This creates the missing journal between the bank account and the account you choose.</p>
              <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="w-full">
                <option value="">Choose an account…</option>
                {accounts.data?.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
              </Select>
            </div>
            {match.isError && <p className="text-xs text-red-600">{(match.error as Error).message}</p>}
          </div>
        )}
      </Modal>

      {/* Exclude */}
      <Modal open={!!excluding} onClose={() => { setExcluding(null); setNote(''); }} title="Exclude this line" footer={<>
        <Button variant="secondary" onClick={() => { setExcluding(null); setNote(''); }}>Cancel</Button>
        <Button onClick={() => exclude.mutate()} disabled={!note || exclude.isPending}>Exclude</Button>
      </>}>
        <p className="mb-3 text-[13px] text-ink-600">For a line that is not ours to account for — a transfer between our own accounts, or a bank error being corrected. It stays visible with your note.</p>
        <Field label="Why?"><Input value={note} onChange={(e) => setNote(e.target.value)} className="w-full" /></Field>
        {exclude.isError && <p className="mt-2 text-xs text-red-600">{(exclude.error as Error).message}</p>}
      </Modal>
    </div>
  );
}
